/**
 * A data file a sheet draws from, fetched once.
 *
 * The structure sheet's page publishes its file at `data.json` beside it,
 * written by `src/assets/worksheet-records.ts`, and names it in
 * `data-cb-data`. The look-alike and vocabulary sheets fetch their keys this
 * way, and their items through `./sheet-shards.ts`. A file that cannot be
 * read leaves the sheet saying so, and the next draw asks again.
 */

/** Fetches one file, once. */
export class SheetData<Shape> {
  readonly #fetch: typeof fetch;
  readonly #address: string;
  readonly #read: (body: unknown) => Shape | undefined;
  #loaded: Promise<Shape | undefined> | undefined;

  constructor(properties: {
    readonly fetch: typeof fetch;
    readonly address: string;
    /** The file's shape, checked, or nothing for a body that is not it. */
    readonly read: (body: unknown) => Shape | undefined;
  }) {
    this.#fetch = properties.fetch;
    this.#address = properties.address;
    this.#read = properties.read;
  }

  /** The file, or nothing where it could not be read. */
  async load(): Promise<Shape | undefined> {
    this.#loaded ??= this.#request();
    const loaded = await this.#loaded;
    if (loaded === undefined) {
      this.#loaded = undefined;
    }

    return loaded;
  }

  async #request(): Promise<Shape | undefined> {
    try {
      const response = await this.#fetch(this.#address);

      return response.ok ? this.#read(await response.json()) : undefined;
    } catch {
      return undefined;
    }
  }
}

/** An object's own keyed values, or nothing for a body that is not an object. */
export function record(
  body: unknown,
): Readonly<Record<string, unknown>> | undefined {
  return typeof body === "object" && body !== null && !Array.isArray(body)
    ? (body as Record<string, unknown>)
    : undefined;
}

/** A list of strings, or nothing for anything else. */
export function strings(value: unknown): readonly string[] | undefined {
  return Array.isArray(value) && value.every((item) => typeof item === "string")
    ? value
    : undefined;
}
